import { useState, type FormEvent } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { useAppStore } from "@/store/useAppStore";
import { cn } from "@/utils/cn";
import { COLORS } from "@/constants/colors";
import { Loader2, User, Lock, Eye, EyeOff } from "lucide-react";

export const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (!username.trim() || !password.trim()) {
      return;
    }

    const success = await login(username, password, rememberMe);
    if (success) {
      // Load app data after successful login
      await useAppStore.getState().initializeApp();
    }
  };

  return (
    <div
      className={cn(
        "min-h-screen flex items-center justify-center p-4",
        COLORS.ui.background.primary
      )}
    >
      <div
        className={cn(
          "w-full max-w-md p-8 rounded-2xl shadow-xl",
          COLORS.ui.background.secondary,
          "border",
          COLORS.ui.border.light
        )}
      >
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <img
            src="/app-icon.png"
            alt="Claude Code History Viewer"
            className="w-16 h-16 mx-auto mb-4"
          />
          <h1 className={cn("text-2xl font-bold", COLORS.ui.text.primary)}>
            Claude History Viewer
          </h1>
          <p className={cn("text-sm mt-2", COLORS.ui.text.muted)}>
            Sign in to view your conversation history
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Username Field */}
          <div>
            <label
              htmlFor="username"
              className={cn(
                "block text-sm font-medium mb-2",
                COLORS.ui.text.secondary
              )}
            >
              Username
            </label>
            <div className="relative">
              <div
                className={cn(
                  "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none",
                  COLORS.ui.text.muted
                )}
              >
                <User className="w-5 h-5" />
              </div>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className={cn(
                  "w-full pl-10 pr-4 py-3 rounded-lg border transition-colors",
                  COLORS.ui.background.white,
                  COLORS.ui.border.light,
                  COLORS.ui.text.primary,
                  "focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500",
                  "placeholder:text-gray-400 dark:placeholder:text-gray-500"
                )}
                autoComplete="username"
                autoFocus
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label
              htmlFor="password"
              className={cn(
                "block text-sm font-medium mb-2",
                COLORS.ui.text.secondary
              )}
            >
              Password
            </label>
            <div className="relative">
              <div
                className={cn(
                  "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none",
                  COLORS.ui.text.muted
                )}
              >
                <Lock className="w-5 h-5" />
              </div>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className={cn(
                  "w-full pl-10 pr-12 py-3 rounded-lg border transition-colors",
                  COLORS.ui.background.white,
                  COLORS.ui.border.light,
                  COLORS.ui.text.primary,
                  "focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500",
                  "placeholder:text-gray-400 dark:placeholder:text-gray-500"
                )}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={cn(
                  "absolute inset-y-0 right-0 pr-3 flex items-center",
                  COLORS.ui.text.muted,
                  "hover:text-gray-600 dark:hover:text-gray-300"
                )}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Remember Me */}
          <div className="flex items-center">
            <input
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className={cn(
                "w-4 h-4 rounded border transition-colors",
                COLORS.ui.border.light,
                "text-orange-500 focus:ring-orange-500/50"
              )}
            />
            <label
              htmlFor="rememberMe"
              className={cn("ml-2 text-sm cursor-pointer", COLORS.ui.text.muted)}
            >
              Remember me for 30 days
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div
              className={cn(
                "p-3 rounded-lg text-sm",
                COLORS.semantic.error.bg,
                COLORS.semantic.error.text
              )}
            >
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !username.trim() || !password.trim()}
            className={cn(
              "w-full py-3 px-4 rounded-lg font-medium transition-all",
              "bg-orange-500 hover:bg-orange-600 text-white",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "focus:outline-none focus:ring-2 focus:ring-orange-500/50"
            )}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Signing in...
              </span>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className={cn("text-xs", COLORS.ui.text.disabled)}>
            Claude Code History Viewer
          </p>
        </div>
      </div>
    </div>
  );
};
